import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from 'next-themes';
import { Sun, Moon, Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function Navbar() {
  const { theme, setTheme, resolvedTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const throttleRef = useRef(null);

  useEffect(() => { setMounted(true); }, []);

  const toggleTheme = useCallback(() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark'), [resolvedTheme, setTheme]);
  const isDark = mounted && resolvedTheme === 'dark';

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      
      // Toujours mettre à jour immédiatement quand on revient en haut
      if (!isScrolled) {
        setScrolled(false);
        throttleRef.current = false;
        return;
      }
      
      // Throttle uniquement en descendant
      if (throttleRef.current) return;
      setScrolled(true);
      throttleRef.current = true;
      setTimeout(() => { throttleRef.current = false; }, 100);
    };
    
    // Vérifier la position initiale du scroll
    handleScroll();
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Services', href: '#services' },
    { label: 'Avantages', href: '#avantages' },
    { label: 'Audit Gratuit', href: '#audit' },
    { label: 'Contact', href: '#contact' },
  ];

  const scrollTo = useCallback((href) => {
    setMobileOpen(false);
    if (location.pathname !== '/') {
      window.location.href = '/' + href;
      return;
    }
    const elementId = href.replace('#', '');
    const el = document.getElementById(elementId);
    el?.scrollIntoView({ behavior: 'smooth' });
  }, [location.pathname]);

  const handleLogoClick = useCallback((e) => {
    e.preventDefault();
    if (location.pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      // Force l'état de la navbar à se réinitialiser immédiatement
      setScrolled(false);
    } else {
      navigate('/');
    }
  }, [location.pathname, navigate]);

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 navbar-blur transition-all duration-300 ${
        scrolled
          ? `${isDark ? 'navbar-dark' : 'navbar-light'} border-b border-border/50`
          : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <a
            href="/"
            onClick={handleLogoClick}
            data-testid="navbar-logo"
            className="text-xl font-bold tracking-tight text-white"
          >
            Fast<span className="text-[hsl(211,100%,50%)] dark:text-[hsl(211,100%,55%)]">Task</span>
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map(link => (
              <button
                key={link.href}
                onClick={() => scrollTo(link.href)}
                data-testid={`nav-link-${link.href.slice(1)}`}
                className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-200"
              >
                {link.label}
              </button>
            ))}
            <button
              onClick={toggleTheme}
              data-testid="theme-toggle"
              className="p-2 rounded-full hover:bg-secondary transition-colors duration-200"
              aria-label="Basculer le thème"
            >
              {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <Button
              onClick={() => scrollTo('#audit')}
              data-testid="nav-cta-button"
              className="rounded-full px-6 h-9 text-sm font-semibold bg-[hsl(211,100%,50%)] hover:bg-[hsl(211,100%,45%)] text-white btn-premium no-color-transition"
            >
              Audit Gratuit
            </Button>
          </div>

          {/* Mobile Controls */}
          <div className="flex md:hidden items-center gap-2">
            <button
              onClick={toggleTheme}
              data-testid="theme-toggle-mobile"
              className="p-2 rounded-full hover:bg-secondary transition-colors"
              aria-label="Basculer le thème"
            >
              {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="mobile-menu-toggle"
              className="p-2 rounded-full hover:bg-secondary transition-colors"
              aria-label="Menu"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div
            data-testid="mobile-menu"
            className="md:hidden pb-6 pt-2 space-y-1 border-t border-border/50"
          >
            {navLinks.map(link => (
              <button
                key={link.href}
                onClick={() => scrollTo(link.href)}
                data-testid={`mobile-nav-${link.href.slice(1)}`}
                className="block w-full text-left py-3 px-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                {link.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
}
